<template>
  <div class="view" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('common.stock')"
      :grid-ref="$refs.smStocksGrid"
      :show-add="false"
      :show-filter="false"
      :show-export="true"
      :show-columns="true"
      :show-refresh="true"
    >
      <template #actions>
        <stock-actions></stock-actions>
      </template>
    </GridPanelHeader>

    <DxDataGrid
      ref="smStocksGrid"
      :data-source="smStocksDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
      @row-expanding="onRowExpanding"
      @row-prepared="onRowPrepared"
    >
      <DxColumn
        data-field="product.name"
        :caption="$t('common.product_name')"
        data-type="string"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="200"
      />
      <DxColumn
        data-field="product.barcode"
        :caption="$t('stock_management.products.barcode')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="150"
      />
      <DxColumn
        data-field="product.code"
        :caption="$t('stock_management.products.code')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="130"
      />
      <DxColumn
        data-field="product_type"
        :caption="$t('stock_management.products.type')"
        data-type="string"
        :allow-sorting="true"
        :allow-filtering="true"
        :min-width="160"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="quantity_mu"
        :caption="$t('common.quantity')"
        :allow-sorting="false"
        :allow-filtering="true"
        :min-width="120"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="warehouse_name"
        :caption="$t('common.warehouse')"
        data-type="string"
        :allow-sorting="true"
        :allow-filtering="true"
        :min-width="150"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="department_name"
        :caption="$t('common.unit')"
        data-type="string"
        :allow-sorting="true"
        :allow-filtering="true"
        :min-width="150"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="partner_name"
        :caption="$t('common.partner')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="160"
      />
      <DxColumn
        data-field="updated_at"
        :caption="$t('common.last_update')"
        data-type="date"
        :allow-sorting="true"
        :format="updatedAtDateTimeFormat"
        :min-width="170"
      />

      <DxMasterDetail :enabled="true" template="lotDetail" />

      <template #lotDetail="{ data }">
        <StockLotsMasterDetail v-if="data?.data?.product?.lot_management" :rowData="data?.data" />
      </template>

      <DxPaging :page-size="pageSize" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import { canDelete, canEdit } from "@/helpers/Permissions";
import StockActions from "~/pages/stock-management/sm-stocks/partials/StockActions.vue";
import StockLotsMasterDetail from "~/pages/stock-management/sm-stocks/partials/StockLotsMasterDetail.vue";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: "SMStockList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      smStocksDataSource: new DataSource({
        store: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const dataSource = {
            data: [],
            totalCount: 0,
          };

          try {
            const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            const payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            const response = await this.getSmStocks(payload);
            dataSource.data = response.data ?? [];
            dataSource.totalCount = response.pagination.total ?? 0;
            return dataSource;
          } catch (error) {
            console.error("Error fetching stocks:", error);
            throw error;
          }
        },
        }),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
      pageSize: 15,
    };
  },
  components: {
    Forbidden,
    StockActions,
    StockLotsMasterDetail,
    GridPanelHeader,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxMasterDetail,
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid sm-stocks-grid",
      };
    },
    updatedAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getSmStocks: "smStocks/getSmStocks",
    }),
    onRowExpanding(e) {
      const visibleRow = e.component.getVisibleRows().find((row) => row.key === e.key);
      const rowData = visibleRow?.data || null;
      if (rowData && !rowData.product?.lot_management) {
        e.cancel = true;
      }
    },
    onRowPrepared(e) {
      if (e.rowType !== "data") {
        return;
      }

      if (!e?.data?.product?.lot_management && e.rowElement?.classList) {
        e.rowElement.classList.add("no-lot-detail");
      }
    },
    onExporting(e) {
      e.fileName = "SMStocks_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SMStocks");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${e.fileName}.xlsx`);
          });
        });
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        this.pageSize = e.value;
        const instance = this.$refs?.smStocksGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
          instance.getDataSource()?.reload?.();
        }
      }
    },
  },
};
</script>

<style scoped>
::v-deep .no-lot-detail .dx-command-expand .dx-datagrid-group-closed {
  visibility: hidden;
}
</style>

<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>
